import { z } from "zod";

/**
 * Flow Account/Contact Notes wire contract. One source of truth for every notes shape that
 * crosses a boundary. Zod only; types are `z.infer`. Pure and import-free of Prisma/express/web.
 */

/** A Salesforce record id: 15 or 18 alphanumerics. Same shape `SalesforceIdSchema` validates. */
export const NotesRecordIdSchema = z
  .string()
  .regex(/^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/);

export const NoteRecordKindSchema = z.enum(["account", "contact"]);
export type NoteRecordKind = z.infer<typeof NoteRecordKindSchema>;

/** A Slack user or workspace-member id (`U…` / `W…`). Teammate mentions carry this, not an SF id. */
export const SlackUserIdSchema = z.string().regex(/^[UW][A-Z0-9]+$/);

export const NoteMentionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), id: SlackUserIdSchema, label: z.string() }),
  z.object({ kind: z.literal("contact"), id: NotesRecordIdSchema, label: z.string() }),
  z.object({ kind: z.literal("account"), id: NotesRecordIdSchema, label: z.string() }),
]);
export type NoteMention = z.infer<typeof NoteMentionSchema>;

export const NoteEntrySchema = z.object({
  recordKind: NoteRecordKindSchema,
  recordId: NotesRecordIdSchema,
  recordName: z.string(),
  accountId: z.string().nullable(),
  lineIndex: z.number().int().nonnegative(),
  author: z.string().nullable(),
  dateText: z.string().nullable(),
  format: z.enum(["terra", "legacy"]),
  /** Always set for well-formed `terra` lines; null for legacy lines whose zone is unresolved. */
  at: z.string().nullable(),
  body: z.string(),
  mentions: z.array(NoteMentionSchema),
});
export type NoteEntry = z.infer<typeof NoteEntrySchema>;

export const NOTE_TEXT_MAX = 200;
export const NOTE_LINE_MAX = 255;
/** 20-char timestamp (`YYYY-MM-DD HH:mm UTC`) plus two ` - ` separators. */
export const NOTE_LINE_OVERHEAD = 26;

/** Trim, collapse whitespace, and replace any ` - ` inside the name with `-` so the
 *  history line stays parseable (a hyphen-with-spaces name is sanitized). */
export function normalizeAuthorName(name: string): string {
  return name.trim().replace(/\s+/g, " ").replace(/ - /g, "-");
}

/** THE canonical note-body transform: trim, newlines and whitespace runs become single spaces. */
export function normalizeNoteText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export function effectiveNoteTextMax(authorName: string): number {
  return Math.min(
    NOTE_TEXT_MAX,
    NOTE_LINE_MAX - NOTE_LINE_OVERHEAD - authorName.trim().length,
  );
}

export function buildNoteLine(input: {
  at: Date;
  authorName: string;
  text: string;
}): string {
  const iso = input.at.toISOString();
  const stamp = `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
  return `${stamp} - ${normalizeAuthorName(input.authorName)} - ${normalizeNoteText(input.text)}`;
}

/** How a mention appears in note text: `@<label>`, tolerating a label that already starts with `@`. */
export function mentionToken(mention: Pick<NoteMention, "label">): string {
  return mention.label.startsWith("@") ? mention.label : `@${mention.label}`;
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The one rule for "is this mention in the text", shared by extraction, pruning, and rendering: a
 * mention's `@label` token that starts the text or follows whitespace, and is not followed by a
 * letter or digit (so `a@Ann.example` and `@Anna` are not `@Ann`; `@Ann,` is). Longest token wins
 * where two overlap (`@Acme Corp` over `@Acme`). One capture group, global + unicode flags: the
 * matches land at odd indexes of `text.split(pattern)`. Callers short-circuit an empty list.
 */
export function mentionTokenPattern(mentions: readonly Pick<NoteMention, "label">[]): RegExp {
  const tokens = [...new Set(mentions.map(mentionToken))].sort((a, b) => b.length - a.length);
  return new RegExp(`(?<!\\S)(${tokens.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}])`, "gu");
}

/** The mentions whose `@label` token appears in `text` per `mentionTokenPattern` (the one rule for server and client). */
export function mentionsInText<M extends Pick<NoteMention, "label">>(
  mentions: readonly M[],
  text: string,
): M[] {
  if (mentions.length === 0) return [];
  const found = new Set(Array.from(text.matchAll(mentionTokenPattern(mentions)), (match) => match[1]));
  return mentions.filter((m) => found.has(mentionToken(m)));
}

export const NoteTargetSchema = z.object({
  kind: NoteRecordKindSchema,
  id: NotesRecordIdSchema,
  /** Nullable: `ContactRow.account_id` is nullable (src/shared/flow/contact-view.ts). */
  accountId: z.string().nullable(),
});
export type NoteTarget = z.infer<typeof NoteTargetSchema>;

/** `a:<id>` / `c:<id>`: the stable key for a target (summaries map, query keys). */
export function noteTargetKey(target: Pick<NoteTarget, "kind" | "id">): string {
  return `${target.kind === "account" ? "a" : "c"}:${target.id}`;
}

export const PostNoteBodySchema = z.object({
  notes: z.string().trim().min(1).max(NOTE_TEXT_MAX),
  clientNoteId: z.string().uuid(),
  mentions: z.array(NoteMentionSchema).max(20).default([]),
  /** Analytics tag; the client never sends name or timestamp. */
  source: z.enum(["composer", "save_as_note"]).default("composer"),
});
export type PostNoteBody = z.infer<typeof PostNoteBodySchema>;

export const NotesSummariesRequestSchema = z.object({
  items: z.array(NoteTargetSchema.pick({ kind: true, id: true })).max(200),
});
export type NotesSummariesRequest = z.infer<typeof NotesSummariesRequestSchema>;

export const NotesSummariesResponseSchema = z.record(
  z.string().regex(/^[ac]:[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/),
  z.object({
    count: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
  }),
);
export type NotesSummariesResponse = z.infer<
  typeof NotesSummariesResponseSchema
>;

export const NotesThreadResponseSchema = z.object({
  target: NoteTargetSchema,
  entries: z.array(NoteEntrySchema),
  roster: z.array(z.object({ id: z.string(), name: z.string() })),
  rosterTruncated: z.boolean(),
  inputs: z.record(z.string(), z.string().nullable()),
  /** The target record's own Name (null when it is not known), for the thread header. */
  targetName: z.string().nullable(),
  /** The thread account's own Name (null when unknown), for the "Posting to" account option. */
  accountName: z.string().nullable().default(null),
});
export type NotesThreadResponse = z.infer<typeof NotesThreadResponseSchema>;

const MentionCandidateSchema = z.object({ id: z.string(), label: z.string() });
export const MentionCandidatesResponseSchema = z.object({
  teammates: z.array(MentionCandidateSchema),
  contacts: z.array(MentionCandidateSchema),
  accounts: z.array(MentionCandidateSchema),
  truncated: z.boolean(),
});
export type MentionCandidatesResponse = z.infer<
  typeof MentionCandidatesResponseSchema
>;
